import { Link } from "react-router-dom";
import { useSession } from "../lib/session.tsx";
import { guideImage } from "../lib/guideImage.ts";

type Localized = { en: string; es: string };

const capabilities = [
  {
    number: "01",
    title: { en: "Command center", es: "Centro de mando" },
    text: {
      en: "A single operational picture for election deadlines, objectives, overdue work, evidence readiness, narrative movement, and compliance alerts.",
      es: "Una sola visión operativa de fechas electorales, objetivos, trabajo vencido, evidencia lista, movimiento narrativo y alertas de cumplimiento.",
    },
  },
  {
    number: "02",
    title: { en: "Evidence-grounded strategy", es: "Estrategia basada en evidencia" },
    text: {
      en: "AURA answers strategic questions from approved campaign evidence, preserves citations, identifies gaps, and quarantines unsupported claims.",
      es: "AURA responde preguntas estratégicas desde evidencia aprobada, conserva citas, identifica vacíos y pone en cuarentena afirmaciones sin respaldo.",
    },
  },
  {
    number: "03",
    title: { en: "Public-opinion watchdog", es: "Vigilante de opinión pública" },
    text: {
      en: "Track public reporting and conversation, monitor narrative velocity, and separate anecdotal signals from verified facts.",
      es: "Sigue cobertura y conversación pública, mide la velocidad narrativa y separa señales anecdóticas de hechos verificados.",
    },
  },
  {
    number: "04",
    title: { en: "Field and constituent operations", es: "Operaciones de campo y comunidad" },
    text: {
      en: "Coordinate contacts, events, assignments, territory, and consent-based live crew positioning without retaining location history.",
      es: "Coordina contactos, eventos, tareas, territorio y ubicación en vivo con consentimiento, sin conservar historial de localización.",
    },
  },
  {
    number: "05",
    title: { en: "Work, decisions, and risk", es: "Trabajo, decisiones y riesgo" },
    text: {
      en: "Connect projects to owners, deadlines, issues, decisions, and risks so campaign work moves from conversation to accountable execution.",
      es: "Conecta proyectos con responsables, fechas, asuntos, decisiones y riesgos para convertir conversaciones en ejecución responsable.",
    },
  },
  {
    number: "06",
    title: { en: "Finance and compliance", es: "Finanzas y cumplimiento" },
    text: {
      en: "Manage vendors, receipts, expenses, filing deadlines, deterministic compliance flags, and separation-of-duties approvals.",
      es: "Administra proveedores, recibos, gastos, fechas de informes, alertas de cumplimiento y aprobaciones con separación de funciones.",
    },
  },
  {
    number: "07",
    title: { en: "Hermes operations engine", es: "Motor operativo Hermes" },
    text: {
      en: "Commission bounded research, planning, and drafting jobs from a private worker. Every output remains a proposal until a person reviews it.",
      es: "Encarga investigación, planificación y redacción delimitadas a un trabajador privado. Cada resultado sigue siendo propuesta hasta su revisión humana.",
    },
  },
  {
    number: "08",
    title: { en: "Bilingual, role-aware access", es: "Acceso bilingüe según el rol" },
    text: {
      en: "English and Spanish interfaces with campaign-specific roles for leadership, strategy, field, finance, communications, and volunteers.",
      es: "Interfaz en español e inglés con roles de campaña para dirección, estrategia, campo, finanzas, comunicación y voluntariado.",
    },
  },
];

const workflow: Array<{ title: Localized; text: Localized }> = [
  {
    title: { en: "Capture", es: "Capturar" },
    text: {
      en: "Bring in documents, public signals, field observations, tasks, expenses, and campaign decisions.",
      es: "Integra documentos, señales públicas, observaciones de campo, tareas, gastos y decisiones de campaña.",
    },
  },
  {
    title: { en: "Verify", es: "Verificar" },
    text: {
      en: "Review evidence, control classification, preserve provenance, and reject unsupported assertions.",
      es: "Revisa evidencia, controla su clasificación, conserva la procedencia y rechaza afirmaciones sin respaldo.",
    },
  },
  {
    title: { en: "Decide", es: "Decidir" },
    text: {
      en: "Ask grounded strategic questions, compare risk, and turn intelligence into accountable choices.",
      es: "Formula preguntas estratégicas fundamentadas, compara riesgos y convierte inteligencia en decisiones responsables.",
    },
  },
  {
    title: { en: "Execute", es: "Ejecutar" },
    text: {
      en: "Assign work, coordinate the field, monitor deadlines, and keep human approval at consequential steps.",
      es: "Asigna trabajo, coordina el campo, vigila fechas y conserva aprobación humana en cada paso importante.",
    },
  },
];

const pricing: Array<{ number: string; title: Localized; text: Localized }> = [
  {
    number: "01",
    title: { en: "Setup · $3,500 one time", es: "Instalación · $3,500 pago único" },
    text: {
      en: "Onboarding, the District 8 race analysis, and a configured campaign overview.",
      es: "Incorporación, el análisis de la contienda del Distrito 8 y un resumen de campaña configurado.",
    },
  },
  {
    number: "02",
    title: { en: "Service + AI · $750 / month", es: "Servicio + IA · $750 / mes" },
    text: {
      en: "Hosting, support, AURA, the watchdog, OCR, and transcription, with AI included under a fair-use allowance. Cancel any time; access runs to the end of the paid month.",
      es: "Hospedaje, soporte, AURA, el vigilante, OCR y transcripción, con IA incluida bajo un límite de uso razonable. Cancele cuando quiera; el acceso sigue hasta el fin del mes pagado.",
    },
  },
  {
    number: "03",
    title: { en: "Guided trial · by invitation", es: "Prueba guiada · por invitación" },
    text: {
      en: "A 14-day trial with full access and $20 of AI usage, whichever comes first, so the candidate and team can evaluate the platform before committing.",
      es: "Una prueba de 14 días con acceso completo y $20 de uso de IA, lo que ocurra primero, para que el candidato y su equipo evalúen la plataforma antes de decidir.",
    },
  },
];

export default function Landing() {
  const { locale, setLocale } = useSession();
  const copy = <T extends Localized>(value: T) => value[locale];
  const es = locale === "es";
  return (
    <div className="landing-page">
      <header className="landing-nav">
        <a className="landing-brand" href="#top" aria-label="Canales CampaignOS home">
          <span className="landing-brand-mark">C8</span>
          <span>
            <strong>Canales CampaignOS</strong>
            <small>{es ? "Gestión + estrategia" : "Management + strategy"}</small>
          </span>
        </a>
        <nav aria-label={es ? "Navegación de la página principal" : "Landing page navigation"}>
          <a href="#platform">{es ? "Plataforma" : "Platform"}</a>
          <a href="#capabilities">{es ? "Funciones" : "Capabilities"}</a>
          <a href="#security">{es ? "Seguridad" : "Security"}</a>
          <a href="#pricing">{es ? "Precios" : "Pricing"}</a>
          <button
            type="button"
            className="subtle"
            onClick={() => setLocale(es ? "en" : "es")}
            aria-label={es ? "Switch to English" : "Cambiar a español"}
          >
            {es ? "EN" : "ES"}
          </button>
          <Link className="landing-signin" to="/login">
            {es ? "Acceso del equipo" : "Staff sign in"} <span aria-hidden="true">↗</span>
          </Link>
        </nav>
      </header>

      <main id="top">
        <section className="landing-hero">
          <div className="landing-orbit landing-orbit-one" aria-hidden="true" />
          <div className="landing-orbit landing-orbit-two" aria-hidden="true" />
          <div className="landing-hero-copy">
            <p className="landing-kicker">
              <span />{" "}
              {es
                ? "Diseñado al ritmo de una campaña real"
                : "Built for the pace of a real campaign"}
            </p>
            <h1>
              {es
                ? "Una campaña no necesita otro tablero."
                : "A campaign does not need another dashboard."}
            </h1>
            <p className="landing-hero-declaration">
              {es ? (
                <>
                  Necesita un <em>sistema de mando.</em>
                </>
              ) : (
                <>
                  It needs a <em>command system.</em>
                </>
              )}
            </p>
            <p className="landing-hero-text">
              {es
                ? "Canales CampaignOS conecta estrategia política, investigación, operaciones de campo, narrativa pública, finanzas, cumplimiento y ejecución diaria en un espacio privado basado en evidencia para la campaña del Distrito 8."
                : "Canales CampaignOS connects political strategy, research, field operations, public narrative, finance, compliance, and daily execution in one private, evidence-first workspace for the District 8 campaign."}
            </p>
            <div className="landing-hero-actions">
              <a className="landing-primary" href="#platform">
                {es ? "Explorar la plataforma" : "Explore the platform"}{" "}
                <span aria-hidden="true">↓</span>
              </a>
              <Link className="landing-secondary" to="/login">
                {es ? "Ingreso de personal autorizado" : "Authorized staff sign in"}
              </Link>
            </div>
          </div>

          <div
            className="landing-hero-visual"
            aria-label="Actual Canales CampaignOS command center screenshot"
          >
            <div className="landing-window-bar">
              <span />
              <span />
              <span />
              <small>{es ? "OPERACIONES DE CAMPAÑA EN VIVO" : "LIVE CAMPAIGN OPERATIONS"}</small>
            </div>
            <img
              src={guideImage("/guide/dashboard.png", locale)}
              alt={
                es
                  ? "Centro de mando de Canales CampaignOS con fechas electorales, opinión pública, evidencia, alertas de cumplimiento y objetivos"
                  : "Canales CampaignOS command center showing election deadlines, public opinion, evidence, compliance alerts, and campaign objectives"
              }
            />
            <div className="landing-visual-tag landing-visual-tag-top">DISTRICT 8 / 2026</div>
            <div className="landing-visual-tag landing-visual-tag-bottom">
              {es ? "PANTALLA REAL DE LA APP" : "REAL APPLICATION SCREEN"}
            </div>
          </div>
        </section>

        <section
          className="landing-proof"
          aria-label={es ? "Principios de la plataforma" : "Platform principles"}
        >
          <div>
            <strong>{es ? "Una sola visión operativa" : "One operating picture"}</strong>
            <span>{es ? "De la estrategia a la ejecución" : "Strategy through execution"}</span>
          </div>
          <div>
            <strong>{es ? "Evidencia antes de afirmar" : "Evidence before assertion"}</strong>
            <span>{es ? "Citas y revisión humana" : "Citations and human review"}</span>
          </div>
          <div>
            <strong>{es ? "Privado por arquitectura" : "Private by architecture"}</strong>
            <span>
              {es ? "Infraestructura dedicada al candidato" : "Candidate-dedicated infrastructure"}
            </span>
          </div>
          <div>
            <strong>Español + English</strong>
            <span>{es ? "Acceso según el rol" : "Role-aware campaign access"}</span>
          </div>
        </section>

        <section className="landing-section landing-platform" id="platform">
          <div className="landing-section-intro">
            <p className="landing-section-number">
              01 / {es ? "EL SISTEMA OPERATIVO" : "THE OPERATING SYSTEM"}
            </p>
            <h2>
              {es
                ? "De la señal a la decisión y a la acción."
                : "From signal to decision to action."}
            </h2>
            <p>
              {es
                ? "CampaignOS reemplaza hojas de cálculo dispersas, chats, carpetas y herramientas de IA desconectadas con un ciclo operativo gobernado que toda la campaña puede entender."
                : "CampaignOS replaces scattered spreadsheets, group chats, folders, and disconnected AI tools with a governed operating loop the whole campaign can understand."}
            </p>
          </div>
          <div className="landing-workflow">
            {workflow.map((item, index) => (
              <article key={item.title.en}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{copy(item.title)}</h3>
                <p>{copy(item.text)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-screenshot-story">
          <div className="landing-story-copy">
            <p className="landing-section-number">
              02 / {es ? "INTELIGENCIA ESTRATÉGICA" : "STRATEGIC INTELLIGENCE"}
            </p>
            <h2>
              {es
                ? "Haz preguntas difíciles. Conserva la evidencia."
                : "Ask hard questions. Keep the receipts."}
            </h2>
            <p>
              {es
                ? "El asesor estratégico AURA combina evidencia interna aprobada con una capa privada de recuperación. Cada afirmación fáctica debe superar la verificación antes de llegar al equipo."
                : "The AURA strategy advisor combines approved internal evidence with a private retrieval layer. Every factual claim must survive verification before it reaches the campaign team."}
            </p>
            <ul>
              <li>
                {es
                  ? "Recuperación de fuentes aprobadas con IDs de cita"
                  : "Approved-source retrieval with citation IDs"}
              </li>
              <li>
                {es
                  ? "Visibilidad de vacíos y fuentes excluidas"
                  : "Evidence gaps and excluded-source visibility"}
              </li>
              <li>
                {es
                  ? "Revisión humana del conocimiento oficial de campaña"
                  : "Human review for canonical campaign knowledge"}
              </li>
              <li>{es ? "Controles de alcance del Distrito 8" : "District 8 scope controls"}</li>
            </ul>
          </div>
          <figure className="landing-screen landing-screen-advisor">
            <div className="landing-screen-label">
              <span>{es ? "APP REAL" : "ACTUAL APP"}</span>{" "}
              {es ? "Asesor Estratégico" : "Strategy Advisor"}
            </div>
            <img
              src={guideImage("/guide/advisor.png", locale)}
              alt={
                es
                  ? "Pantalla real del Asesor Estratégico con formulario de investigación del Distrito 8"
                  : "Actual Strategy Advisor screen in Canales CampaignOS with a District 8 research question form"
              }
              loading="lazy"
            />
          </figure>
        </section>

        <section className="landing-section landing-screenshot-story landing-story-reverse">
          <figure className="landing-screen landing-screen-watchdog">
            <div className="landing-screen-label">
              <span>{es ? "APP REAL" : "ACTUAL APP"}</span>{" "}
              {es ? "Vigilante de Opinión Pública" : "Public Opinion Watchdog"}
            </div>
            <img
              src={guideImage("/guide/watchdog.png", locale)}
              alt={
                es
                  ? "Pantalla real del Vigilante con menciones, sentimiento, fuentes y movimiento narrativo"
                  : "Actual Public Opinion Watchdog screen showing mentions, sentiment, sources, and narrative movement"
              }
              loading="lazy"
            />
          </figure>
          <div className="landing-story-copy">
            <p className="landing-section-number">
              03 / {es ? "CONCIENCIA NARRATIVA" : "NARRATIVE AWARENESS"}
            </p>
            <h2>
              {es
                ? "Observa cómo se mueve la historia antes de que se convierta en noticia."
                : "See the story moving before it becomes the story."}
            </h2>
            <p>
              {es
                ? "El Vigilante organiza cobertura y conversación pública como señal anecdótica —no como encuesta ni sustituto de evidencia verificada— para detectar temprano riesgos, impulso y asuntos de conducta del candidato."
                : "Watchdog organizes public coverage and conversation into an anecdotal signal—not a poll and never a substitute for verified evidence—so strategists can spot risk, momentum, and candidate-conduct concerns early."}
            </p>
            <div className="landing-metric-row">
              <div>
                <strong>3×</strong>
                <span>{es ? "rastreos programados al día" : "scheduled sweeps daily"}</span>
              </div>
              <div>
                <strong>5</strong>
                <span>{es ? "niveles de altitud narrativa" : "narrative altitude levels"}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-section landing-capabilities" id="capabilities">
          <div className="landing-section-intro landing-section-intro-wide">
            <p className="landing-section-number">04 / {es ? "FUNCIONES" : "CAPABILITIES"}</p>
            <h2>
              {es
                ? "Toda la campaña trabajando desde la misma verdad."
                : "The entire campaign, working from the same truth."}
            </h2>
          </div>
          <div className="landing-capability-grid">
            {capabilities.map((capability) => (
              <article key={capability.number}>
                <span>{capability.number}</span>
                <h3>{copy(capability.title)}</h3>
                <p>{copy(capability.text)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-dual-screens">
          <div className="landing-dual-heading">
            <p className="landing-section-number">
              05 / {es ? "OPERACIONES EN EL MUNDO REAL" : "OPERATIONS IN THE REAL WORLD"}
            </p>
            <h2>
              {es ? (
                <>
                  Coordina la calle.
                  <br />
                  Protege las cuentas.
                </>
              ) : (
                <>
                  Coordinate the street.
                  <br />
                  Protect the books.
                </>
              )}
            </h2>
          </div>
          <figure className="landing-screen">
            <div className="landing-screen-label">
              <span>{es ? "APP REAL" : "ACTUAL APP"}</span> {es ? "Mapa de Campo" : "Field Map"}
            </div>
            <img
              src={guideImage("/guide/field.png", locale)}
              alt={
                es
                  ? "Mapa real de campo para coordinación en vivo con consentimiento"
                  : "Actual CampaignOS field map for consent-based live team coordination"
              }
              loading="lazy"
            />
          </figure>
          <figure className="landing-screen landing-screen-offset">
            <div className="landing-screen-label">
              <span>{es ? "APP REAL" : "ACTUAL APP"}</span>{" "}
              {es ? "Finanzas + Cumplimiento" : "Finance + Compliance"}
            </div>
            <img
              src={guideImage("/guide/finance.png", locale)}
              alt={
                es
                  ? "Pantalla real de finanzas y cumplimiento con alertas y transacciones"
                  : "Actual CampaignOS finance and compliance screen showing compliance alerts and transactions"
              }
              loading="lazy"
            />
          </figure>
        </section>

        <section className="landing-security" id="security">
          <div className="landing-security-mark" aria-hidden="true">
            {es ? (
              <>
                HUMANO
                <br />
                EN EL
                <br />
                CICLO
              </>
            ) : (
              <>
                HUMAN
                <br />
                IN THE
                <br />
                LOOP
              </>
            )}
          </div>
          <div>
            <p className="landing-section-number">
              06 / {es ? "GOBERNANZA POR DISEÑO" : "GOVERNANCE BY DESIGN"}
            </p>
            <h2>
              {es ? (
                <>
                  La IA puede acelerar el trabajo.
                  <br />
                  No tiene la última palabra.
                </>
              ) : (
                <>
                  AI can accelerate the work.
                  <br />
                  It does not get the final word.
                </>
              )}
            </h2>
          </div>
          <div className="landing-security-copy">
            <p>
              {es
                ? "El entorno de Canales CampaignOS está aislado para esta campaña, con su propio proyecto Firebase, redes privadas de contenedores, almacenamiento persistente, perfil Hermes, conjunto RAGFlow y respaldos cifrados."
                : "The Canales CampaignOS environment is isolated to this campaign, with its own Firebase project, private container networks, persistent storage, Hermes profile, RAGFlow dataset, and encrypted backups."}
            </p>
            <p>
              {es
                ? "Los resultados importantes permanecen como propuestas. La aprobación de evidencia, revisión de afirmaciones, autorización financiera, cumplimiento, publicación y eliminación permanecen bajo control humano."
                : "Consequential outputs remain proposals. Evidence approval, claim review, financial approval, compliance disposition, publishing, and deletion stay under human control."}
            </p>
          </div>
        </section>

        <section className="landing-section landing-capabilities" id="pricing">
          <div className="landing-section-intro landing-section-intro-wide">
            <p className="landing-section-number">07 / {es ? "PRECIOS" : "PRICING"}</p>
            <h2>
              {es
                ? "Una instalación. Una mensualidad. IA incluida."
                : "One setup fee. One monthly fee. AI included."}
            </h2>
          </div>
          <div className="landing-capability-grid">
            {pricing.map((plan) => (
              <article key={plan.number}>
                <span>{plan.number}</span>
                <h3>{copy(plan.title)}</h3>
                <p>{copy(plan.text)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-cta">
          <p className="landing-kicker">
            <span /> Chris Canales · El Paso City Council · District 8
          </p>
          <h2>
            {es ? (
              <>
                Una campaña.
                <br />
                Una sola visión operativa.
              </>
            ) : (
              <>
                One campaign.
                <br />
                One operating picture.
              </>
            )}
          </h2>
          <p>
            {es
              ? "Diseñado para que un equipo disciplinado investigue, decida, coordine y ejecute."
              : "Built to help a disciplined team research, decide, coordinate, and execute."}
          </p>
          <Link className="landing-primary" to="/login">
            {es ? "Entrar al espacio del equipo" : "Enter the staff workspace"}{" "}
            <span aria-hidden="true">↗</span>
          </Link>
        </section>
      </main>

      <footer className="landing-footer">
        <div>
          <strong>Canales CampaignOS</strong>
          <span>
            {es
              ? "Plataforma privada de gestión y estrategia de campaña."
              : "Private campaign management and strategy platform."}
          </span>
        </div>
        <div>
          Built by{" "}
          <a href="https://elevateelpaso.com" target="_blank" rel="noreferrer noopener">
            Elevate El Paso
          </a>
        </div>
        <div>© 2026 · {es ? "Uso autorizado de campaña" : "Authorized campaign use"}</div>
      </footer>
    </div>
  );
}
